import React, { createContext, useEffect, useState, useCallback } from "react";
import { getMyData } from "../api/auth";

let logoutTimer;

// setTimeout accepts max ~24.8 days before 32-bit integer overflow fires it immediately
const MAX_TIMER_MS = 2_147_483_647;

const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const resolveStorage = () => {
  if (typeof window === "undefined") {
    return noopStorage;
  }
  try {
    const { localStorage } = window;
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return localStorage;
  } catch (error) {
    console.warn(
      "Local storage is not available; applying in-memory fallback.",
      error,
    );
    return noopStorage;
  }
};
const storage = resolveStorage();

export const UserContext = createContext({
  token: "",
  isLoggedIn: false,
  userSet: () => {},
  user: undefined,
  logout: () => {},
  login: () => {},
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();

  return adjExpirationTime - currentTime;
};

const retrieveStoredToken = () => {
  const userToken = storage.getItem("RediHomeToken");
  const storedExpirationDate = storage.getItem("ExpirationTime");

  if (!userToken || !storedExpirationDate) {
    storage.removeItem("RediHomeToken");
    storage.removeItem("ExpirationTime");
    storage.removeItem("RediHomeUser");
    return null;
  }

  const remainingTime = calculateRemainingTime(storedExpirationDate);

  if (remainingTime <= 3600) {
    storage.removeItem("RediHomeToken");
    storage.removeItem("ExpirationTime");
    storage.removeItem("RediHomeUser");
    return null;
  }

  return {
    token: userToken,
    duration: remainingTime,
  };
};

const retrieveStoredUser = () => {
  const user = storage.getItem("RediHomeUser");

  return user;
};

const removeStoredUser = () => {
  storage.removeItem("RediHomeUser");
  return null;
};

export const getBoolStorage = (item) => {
  return storage.getItem(item) === "true";
};

export const UserProvider = (props) => {
  const tokenData = retrieveStoredToken();
  const userData = retrieveStoredUser();

  let initialToken;
  if (tokenData) {
    initialToken = tokenData.token;
  }
  let initialUser;
  if (userData) {
    initialUser = JSON.parse(userData);
  }

  const [token, setToken] = useState(initialToken);
  const [user, setUser] = useState(initialUser);

  const userIsLoggedIn = !!token;

  const logoutHandler = useCallback(() => {
    setToken(null);
    setUser(removeStoredUser());
    storage.removeItem("RediHomeToken");
    storage.removeItem("ExpirationTime");
    storage.removeItem("RediHomeUser");
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
  }, []);

  const userSetHandler = useCallback(async () => {
    let user = await getMyData(token);
    if (user?.isOk) {
      setUser(user.data);
      storage.setItem("RediHomeUser", JSON.stringify(user.data));
      return user.data;
    }
    return undefined;
  }, [token]);

  const loginHandler = async (token, expirationTime) => {
    setToken(token);
    storage.setItem("RediHomeToken", token);
    // Always store as ISO string for consistent cross-platform parsing
    const expirationIso =
      expirationTime instanceof Date
        ? expirationTime.toISOString()
        : new Date(expirationTime).toISOString();
    storage.setItem("ExpirationTime", expirationIso);

    const remainingTime = calculateRemainingTime(expirationIso);

    logoutTimer = setTimeout(
      logoutHandler,
      Math.min(remainingTime, MAX_TIMER_MS),
    );
    try {
      let user = await getMyData(token);
      if (user.isOk) {
        setUser(user.data);
        storage.setItem("RediHomeUser", JSON.stringify(user.data));
      }
      return user?.data;
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    if (tokenData) {
      logoutTimer = setTimeout(
        logoutHandler,
        Math.min(tokenData.duration, MAX_TIMER_MS),
      );
    }
  }, [tokenData, logoutHandler]);

  // Payments open the operator in a second tab, so the tab that finishes the
  // purchase is not the one the user comes back to. Picking up the refreshed
  // account from storage keeps the tab left behind from claiming that the
  // subscription has expired until it is reloaded by hand.
  useEffect(() => {
    const syncUserFromStorage = (event) => {
      if (event.key !== "RediHomeUser" || !event.newValue) {
        return;
      }
      try {
        setUser(JSON.parse(event.newValue));
      } catch (error) {
        console.warn("Nie udało się odczytać danych użytkownika.", error);
      }
    };

    window.addEventListener("storage", syncUserFromStorage);
    return () => window.removeEventListener("storage", syncUserFromStorage);
  }, []);

  const contextValue = {
    user: user,
    token: token,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
    userSet: userSetHandler,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {props?.children}
    </UserContext.Provider>
  );
};
